/* Importar documentos: la carpeta de clientes entera, de una vez.
 *
 * Quien llega a Tabularis trae años de documentos en carpetas con el nombre de
 * cada cliente —en el computador o en Google Drive— y subirlos caso por caso
 * es lo que frena la migración. Acá se arrastra la carpeta (o los .zip que
 * entrega Drive al descargarla), cada carpeta se cruza con su caso y el
 * abogado revisa antes de que se suba nada. Lo que no tiene caso se crea.
 *
 * Lo que no se puede permitir es un documento en el caso equivocado. Por eso:
 * el cruce es por reglas exactas con su nivel de certeza a la vista (ver
 * utils/importarCarpetas.ts), lo dudoso no se decide solo, se confirma antes
 * de subir, y al final queda un informe con el destino de CADA archivo.
 *
 * Volver a importar la misma carpeta es seguro: lo que ya está en el caso con
 * el mismo nombre y tamaño se salta, y los casos creados acá recuerdan de qué
 * carpeta salieron, así que no se crean dos veces.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as FS from '../../utils/datos';
import {
  NUEVO, agrupar, filtrar, fmtBytes, leerArrastre, leerSeleccion, mejorNivel, nivelesPosibles, nombreDeCliente, tipoPorNombre,
  type ArchivoConRuta, type Certeza, type Grupo,
} from '../../utils/importarCarpetas';

const OMITIR = '__omitir__';
/** "Junto con otra causa de la misma carpeta": para los archivos sueltos de
 *  un cliente con varias causas. Se resuelve al importar, después de crear. */
const JUNTO = 'grupo:';
/** Subidas en paralelo. Más que esto no acelera en una conexión de oficina y
 *  sí hace que un corte deje más archivos a medias. */
const EN_PARALELO = 3;

type Fase = 'elegir' | 'leyendo' | 'revisar' | 'importando' | 'listo';
type Filtro = 'todas' | 'revisar' | 'nombre' | 'nuevo';
type Fila = { carpeta: string; codigo: string; archivo: string; caso: string; resultado: string };

const ESTADO: Record<Certeza | 'manual', { texto: string; color: string; ayuda: string }> = {
  carpeta: { texto: '✓ Ya importada', color: '#66BB6A', ayuda: 'Esta carpeta ya se importó antes a este caso' },
  codigo: { texto: '✓ Por código', color: '#66BB6A', ayuda: 'El código del caso coincide exacto' },
  nombre: { texto: '≈ Por nombre', color: '#E5C46B', ayuda: 'El nombre del cliente se parece: confirma que sea el mismo' },
  nuevo: { texto: '＋ Caso nuevo', color: '#64B5F6', ayuda: 'No se parece a ningún caso: se creará' },
  revisar: { texto: '⚠ Elige', color: '#FFB74D', ayuda: 'Hay más de una posibilidad: elige tú' },
  manual: { texto: '✎ Elegido por ti', color: '#D6E4F4', ayuda: 'Lo cambiaste a mano' },
};

const csv = (filas: Fila[]) =>
  '﻿' + ['Carpeta;Código;Archivo;Caso;Resultado', ...filas.map(f =>
    [f.carpeta, f.codigo, f.archivo, f.caso, f.resultado].map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))].join('\r\n');

const ImportarDocumentos: React.FC = () => {
  const [casos, setCasos] = useState<any[] | null>(null);
  const [abogadas, setAbogadas] = useState<any[]>([]);
  const [captadoras, setCaptadoras] = useState<any[]>([]);
  const [captadorObligatorio, setCaptadorObligatorio] = useState(false);

  const [fase, setFase] = useState<Fase>('elegir');
  const [lista, setLista] = useState<ArchivoConRuta[]>([]);
  const [descartados, setDescartados] = useState<{ basura: string[]; deGoogle: string[] }>({ basura: [], deGoogle: [] });
  const [nivel, setNivel] = useState(0);
  const [destino, setDestino] = useState<Record<string, string>>({});
  const [manual, setManual] = useState<Set<string>>(new Set());
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [nuevoAbogado, setNuevoAbogado] = useState('');
  const [nuevoCaptador, setNuevoCaptador] = useState('');
  const [encima, setEncima] = useState(false);
  const [aviso, setAviso] = useState('');

  const [progreso, setProgreso] = useState({ hechos: 0, total: 0, bytes: 0, totalBytes: 0, carpeta: '' });
  const [resumen, setResumen] = useState({ subidos: 0, repetidos: 0, creados: 0, errores: 0, cancelado: false });
  const [informe, setInforme] = useState<Fila[]>([]);
  const cancelar = useRef(false);
  const carpetaRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    FS.loadCasos('', '').then(setCasos).catch(() => setCasos([]));
    FS.loadAbogadas().then(setAbogadas).catch(() => {});
    FS.loadCaptadoras().then(setCaptadoras).catch(() => {});
    FS.loadAjustes().then((a: any) => setCaptadorObligatorio(!!a?.casos?.captadorObligatorio)).catch(() => {});
  }, []);

  // Cerrar la pestaña a mitad de una importación la corta sin aviso.
  useEffect(() => {
    if (fase !== 'importando') return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [fase]);

  const paraCruce = useMemo(
    () => (casos || []).map(c => ({
      id: c.id, nombreCliente: c.nombreCliente, rut: c.rut, codigoCaso: c.codigoCaso,
      codigoOrigen: c.codigoOrigen, carpetaOrigen: c.carpetaOrigen,
    })),
    [casos],
  );
  const casoPorId = useMemo(() => new Map((casos || []).map(c => [c.id, c])), [casos]);
  const casosOrdenados = useMemo(
    () => [...(casos || [])].sort((a, b) => (a.nombreCliente || '').localeCompare(b.nombreCliente || '', 'es')),
    [casos],
  );
  const { grupos, sueltos } = useMemo(() => agrupar(lista, paraCruce, nivel), [lista, paraCruce, nivel]);
  const niveles = useMemo(() => nivelesPosibles(lista), [lista]);
  const grupoPorClave = useMemo(() => new Map(grupos.map(g => [g.clave, g])), [grupos]);

  // Al cambiar la carpeta o el nivel, lo propuesto vuelve a ser la elección.
  useEffect(() => {
    setDestino(Object.fromEntries(grupos.map(g => [g.clave, g.propuesto || ''])));
    setManual(new Set());
  }, [grupos]);

  const recibir = (todo: ArchivoConRuta[]) => {
    const { utiles, basura, deGoogle } = filtrar(todo);
    setDescartados({ basura, deGoogle });
    if (!utiles.length) {
      setAviso(deGoogle.length
        ? 'Esa carpeta solo tiene accesos directos de Google Docs, no los documentos. Descárgala desde drive.google.com (clic derecho → Descargar) y arrastra el .zip.'
        : 'No se encontraron archivos para importar.');
      setFase('elegir');
      return;
    }
    setAviso('');
    setLista(utiles);
    setNivel(mejorNivel(utiles, paraCruce));
    setFiltro('todas');
    setFase('revisar');
  };
  const leer = async (obtener: () => Promise<ArchivoConRuta[]>) => {
    setFase('leyendo');
    try { recibir(await obtener()); }
    catch (e: any) { setAviso(e?.message || 'No se pudo leer lo que elegiste.'); setFase('elegir'); }
  };
  const alSoltar = (e: React.DragEvent) => {
    e.preventDefault();
    setEncima(false);
    if (fase === 'importando' || !casos) return;
    const items = e.dataTransfer.items;
    leer(() => leerArrastre(items));
  };
  const alElegir = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) leer(() => leerSeleccion(files));
    e.target.value = '';
  };

  const elegir = (clave: string, valor: string) => {
    setDestino(d => ({ ...d, [clave]: valor }));
    setManual(m => new Set(m).add(clave));
  };

  /** El caso al que va un grupo, siguiendo "junto con otra causa". */
  const destinoFinal = (g: Grupo): string => {
    const v = destino[g.clave] || '';
    if (!v.startsWith(JUNTO)) return v;
    const otro = grupoPorClave.get(v.slice(JUNTO.length));
    return otro ? destino[otro.clave] || '' : '';
  };
  const conDestino = grupos.filter(g => { const v = destinoFinal(g); return v && v !== OMITIR; });
  const porRevisar = grupos.filter(g => !destinoFinal(g));
  const nuevos = grupos.filter(g => destino[g.clave] === NUEVO);
  const aExistentes = new Set(conDestino.map(destinoFinal).filter(v => v !== NUEVO)).size;
  const archivosAImportar = conDestino.reduce((n, g) => n + g.archivos.length, 0);
  const bytesAImportar = conDestino.reduce((n, g) => n + g.bytes, 0);
  const faltaCaptador = nuevos.length > 0 && captadorObligatorio && !nuevoCaptador;

  const estadoDe = (g: Grupo) => (manual.has(g.clave) ? 'manual' : g.certeza);
  const cuenta = (c: Certeza) => grupos.filter(g => !manual.has(g.clave) && g.certeza === c).length;

  const importar = async () => {
    if (!conDestino.length || faltaCaptador) return;
    const ok = window.confirm(
      `Se van a subir ${archivosAImportar.toLocaleString('es-CL')} archivos (${fmtBytes(bytesAImportar)}):\n\n` +
      (aExistentes ? `• a ${aExistentes} caso${aExistentes !== 1 ? 's' : ''} que ya existe${aExistentes !== 1 ? 'n' : ''}\n` : '') +
      (nuevos.length ? `• a ${nuevos.length} caso${nuevos.length !== 1 ? 's' : ''} nuevo${nuevos.length !== 1 ? 's' : ''} que se crea${nuevos.length !== 1 ? 'n' : ''} ahora\n` : '') +
      (porRevisar.length ? `\n${porRevisar.length} carpeta${porRevisar.length !== 1 ? 's' : ''} sin caso elegido NO se importa${porRevisar.length !== 1 ? 'n' : ''}.\n` : '') +
      '\nNo se borra ni se reemplaza nada de lo que ya está en Tabularis. ¿Continuar?',
    );
    if (!ok) return;

    cancelar.current = false;
    setFase('importando');
    const filas: Fila[] = [];
    const res = { subidos: 0, repetidos: 0, creados: 0, errores: 0, cancelado: false };
    const avance = { hechos: 0, bytes: 0 };
    setProgreso({ hechos: 0, total: archivosAImportar, bytes: 0, totalBytes: bytesAImportar, carpeta: '' });

    const abogada = abogadas.find(a => a.id === nuevoAbogado);
    const captadora = captadoras.find(c => c.id === nuevoCaptador);
    const resuelto = new Map<string, { id: string; etiqueta: string }>();
    const etiqueta = (c: any) => `${c?.codigoCaso || ''} · ${c?.nombreCliente || ''}`;

    // Los que van "junto con otra causa" al final: esa causa puede crearse acá.
    const esJunto = (g: Grupo) => Number((destino[g.clave] || '').startsWith(JUNTO));
    const orden = [...conDestino].sort((a, b) => esJunto(a) - esJunto(b));
    for (const g of orden) {
      if (cancelar.current) break;
      setProgreso(p => ({ ...p, carpeta: g.codigo ? `${g.carpeta} / ${g.codigo}` : g.carpeta }));
      const v = destino[g.clave];
      let caso: { id: string; etiqueta: string } | undefined;
      try {
        if (v.startsWith(JUNTO)) {
          caso = resuelto.get(v.slice(JUNTO.length));
          if (!caso) throw new Error('su causa no se pudo importar');
        } else if (v === NUEVO) {
          // Lo mínimo para que el caso exista. Queda anotada la carpeta (y el
          // código) de la que salió: así reimportar lo encuentra y no lo
          // duplica. El resto (teléfono, materia) se completa en el caso.
          const { nombre, rut } = nombreDeCliente(g.carpeta);
          const creado = await FS.crearCaso({
            nombreCliente: nombre, ...(rut ? { rut } : {}),
            carpetaOrigen: g.carpeta, ...(g.codigo ? { codigoOrigen: g.codigo } : {}),
            abogada: abogada ? (abogada.nombre || abogada.email) : '', abogadaId: nuevoAbogado,
            captadora: captadora ? (captadora.nombre || captadora.email) : '', captadoraId: nuevoCaptador,
          });
          caso = { id: creado.id, etiqueta: `${creado.codigo} · ${nombre} (nuevo)` };
          res.creados++;
        } else {
          caso = { id: v, etiqueta: etiqueta(casoPorId.get(v)) };
        }
        resuelto.set(g.clave, caso);
      } catch (e: any) {
        for (const a of g.archivos) filas.push({ carpeta: g.carpeta, codigo: g.codigo || '', archivo: a.nombre, caso: '', resultado: `NO IMPORTADO: no se pudo crear el caso (${e?.message || 'error'})` });
        res.errores += g.archivos.length;
        avance.hechos += g.archivos.length; avance.bytes += g.bytes;
        setProgreso(p => ({ ...p, ...avance }));
        continue;
      }

      // Lo que ya está en el caso no se vuelve a subir: así se retoma una
      // importación cortada sin duplicar nada.
      let yaEstan = new Set<string>();
      try { yaEstan = new Set((await FS.loadDocumentos(caso.id)).map((d: any) => `${d.nombre}|${d.size ?? ''}`)); }
      catch { /* sin lista, se sube todo */ }

      const destinoCaso = caso;
      const cola = [...g.archivos];
      const obrero = async () => {
        while (cola.length && !cancelar.current) {
          const a = cola.shift()!;
          const fila: Fila = { carpeta: g.carpeta, codigo: g.codigo || '', archivo: a.nombre, caso: destinoCaso.etiqueta, resultado: '' };
          if (yaEstan.has(`${a.nombre}|${a.archivo.size}`)) { res.repetidos++; fila.resultado = 'ya estaba en el caso'; }
          else {
            try {
              const blob = await a.archivo.leer();
              const file = new File([blob], a.nombre, { type: blob.type || tipoPorNombre(a.nombre) });
              await FS.subirDocumento(destinoCaso.id, file, a.nombre, 'Importado');
              res.subidos++; fila.resultado = 'importado';
            } catch (e: any) { res.errores++; fila.resultado = `NO IMPORTADO: ${e?.message || 'error'}`; }
          }
          filas.push(fila);
          avance.hechos++; avance.bytes += a.archivo.size;
          setProgreso(p => ({ ...p, ...avance }));
        }
      };
      await Promise.all(Array.from({ length: Math.min(EN_PARALELO, cola.length) }, obrero));
    }
    res.cancelado = cancelar.current;

    // El informe da cuenta de TODO lo que se arrastró, no solo de lo subido.
    const hechos = new Set(filas.map(f => `${f.carpeta}|${f.codigo}|${f.archivo}`));
    for (const g of grupos) for (const a of g.archivos) {
      if (hechos.has(`${g.carpeta}|${g.codigo || ''}|${a.nombre}`)) continue;
      const v = destinoFinal(g);
      filas.push({
        carpeta: g.carpeta, codigo: g.codigo || '', archivo: a.nombre, caso: '',
        resultado: v === OMITIR ? 'no importado: marcaste no importar'
          : !v ? 'NO IMPORTADO: sin caso elegido'
            : 'NO IMPORTADO: se detuvo la importación',
      });
    }
    for (const s of sueltos) filas.push({ carpeta: '', codigo: '', archivo: s.ruta, caso: '', resultado: 'no importado: fuera de una carpeta de cliente' });
    for (const r of descartados.deGoogle) filas.push({ carpeta: '', codigo: '', archivo: r, caso: '', resultado: 'no importado: acceso directo de Google Docs' });
    for (const r of descartados.basura) filas.push({ carpeta: '', codigo: '', archivo: r, caso: '', resultado: 'no importado: archivo del sistema o vacío' });

    setInforme(filas);
    setResumen(res);
    setFase('listo');
    FS.loadCasos('', '').then(setCasos).catch(() => {});
  };

  const descargarInforme = () => {
    const url = URL.createObjectURL(new Blob([csv(informe)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `importacion-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const reiniciar = () => { setLista([]); setDestino({}); setFase('elegir'); setAviso(''); setInforme([]); };

  const etiquetaCaso = (c: any) => `${c?.nombreCliente || 'Sin nombre'}${c?.codigoCaso ? ` · ${c.codigoCaso}` : ''}`;
  const ejemplo = (d: number) => {
    const nombres = Array.from(new Set(lista.map(x => x.ruta.split('/').filter(Boolean)).filter(r => r.length > d + 1).map(r => r[d])));
    return nombres.slice(0, 3).join(', ') + (nombres.length > 3 ? ` y ${nombres.length - 3} más` : '');
  };

  /* ── Elegir la carpeta ─────────────────────────────────────────────── */
  if (fase === 'elegir' || fase === 'leyendo') {
    return (
      <div>
        <div className="p-section-title">Importar documentos</div>
        <p style={{ fontSize: 13, color: '#9BB0CC', lineHeight: 1.6, maxWidth: 760, marginTop: 0 }}>
          Trae de una vez la carpeta donde guardas los documentos de tus clientes, con una subcarpeta por cliente
          (y, si las usas, una subcarpeta por causa con su código). Cada carpeta se cruza con su caso por el código,
          el RUT o el nombre del cliente; lo que no tiene caso se crea. Revisas todo antes de que se suba nada, y no
          se borra ni se reemplaza nada de lo que ya está en Tabularis.
        </p>
        <div
          onDragOver={e => { e.preventDefault(); setEncima(true); }}
          onDragLeave={() => setEncima(false)}
          onDrop={alSoltar}
          style={{
            border: `2px dashed ${encima ? 'var(--gold)' : 'rgba(191,175,114,0.35)'}`, borderRadius: 12,
            background: encima ? 'rgba(191,175,114,0.08)' : 'rgba(255,255,255,0.02)',
            padding: '42px 20px', textAlign: 'center', maxWidth: 760, transition: 'all 0.15s',
          }}>
          {fase === 'leyendo' || !casos ? (
            // Sin los casos cargados el cruce saldría vacío: se espera.
            <div style={{ color: '#D6E4F4', fontSize: 14 }}>⏳ {casos ? 'Leyendo…' : 'Cargando tus casos…'}</div>
          ) : (
            <>
              <div style={{ fontSize: 34, marginBottom: 6 }}>📁</div>
              <div style={{ color: '#D6E4F4', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Arrastra aquí tu carpeta de clientes, o los .zip de Google Drive</div>
              <div style={{ color: '#9BB0CC', fontSize: 12.5, marginBottom: 16 }}>o elígelos desde tu computador</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="p-btn-secondary" onClick={() => carpetaRef.current?.click()}>📂 Elegir carpeta</button>
                <button className="p-btn-secondary" onClick={() => zipRef.current?.click()}>🗜️ Elegir .zip</button>
              </div>
              <input ref={carpetaRef} type="file" multiple onChange={alElegir} style={{ display: 'none' }}
                {...({ webkitdirectory: '', directory: '' } as any)} />
              <input ref={zipRef} type="file" multiple accept=".zip,application/zip" onChange={alElegir} style={{ display: 'none' }} />
            </>
          )}
        </div>
        {aviso && <div style={{ marginTop: 14, maxWidth: 760, fontSize: 13, color: '#FFB74D', lineHeight: 1.5 }}>⚠️ {aviso}</div>}
        <div style={{ marginTop: 22, maxWidth: 760, fontSize: 12.5, color: '#9BB0CC', lineHeight: 1.7 }}>
          <div style={{ color: '#D6E4F4', fontWeight: 600, marginBottom: 4 }}>¿Tus documentos están en Google Drive?</div>
          En drive.google.com, clic derecho sobre tu carpeta de clientes → <em>Descargar</em>. Drive la entrega como uno o
          varios .zip (los parte cuando es grande): arrástralos <strong>todos juntos</strong> aquí, sin descomprimirlos.
          Los Google Docs y Sheets vienen convertidos a Word y Excel.
        </div>
      </div>
    );
  }

  /* ── Importando ─────────────────────────────────────────────────────── */
  if (fase === 'importando') {
    const pct = progreso.totalBytes ? Math.round((progreso.bytes / progreso.totalBytes) * 100) : 0;
    return (
      <div style={{ maxWidth: 720 }}>
        <div className="p-section-title">Importando documentos…</div>
        <div style={{ height: 10, background: 'rgba(255,255,255,0.08)', borderRadius: 6, overflow: 'hidden', margin: '14px 0 10px' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: 'var(--gold)', transition: 'width 0.3s' }} />
        </div>
        <div style={{ fontSize: 13, color: '#D6E4F4' }}>
          {progreso.hechos.toLocaleString('es-CL')} de {progreso.total.toLocaleString('es-CL')} archivos · {fmtBytes(progreso.bytes)} de {fmtBytes(progreso.totalBytes)}
        </div>
        <div style={{ fontSize: 12, color: '#9BB0CC', marginTop: 4 }}>{progreso.carpeta}</div>
        <div style={{ fontSize: 12, color: '#9BB0CC', marginTop: 14, lineHeight: 1.5 }}>
          No cierres esta pestaña mientras se importa. Si se corta, vuelve a arrastrar lo mismo: lo que ya se subió se salta y no se crean casos repetidos.
        </div>
        <button className="p-btn-secondary" style={{ marginTop: 16 }} onClick={() => { cancelar.current = true; }}>Detener</button>
      </div>
    );
  }

  /* ── Listo ──────────────────────────────────────────────────────────── */
  if (fase === 'listo') {
    const noImportados = informe.filter(f => f.resultado.startsWith('NO IMPORTADO'));
    return (
      <div style={{ maxWidth: 760 }}>
        <div className="p-section-title">{resumen.cancelado ? 'Importación detenida' : 'Importación terminada'}</div>
        <div style={{ fontSize: 14, color: '#D6E4F4', lineHeight: 1.8 }}>
          ✅ {resumen.subidos.toLocaleString('es-CL')} documento{resumen.subidos !== 1 ? 's' : ''} importado{resumen.subidos !== 1 ? 's' : ''}
          {resumen.creados > 0 && <><br />➕ {resumen.creados} caso{resumen.creados !== 1 ? 's' : ''} nuevo{resumen.creados !== 1 ? 's' : ''}: complétales el teléfono, la materia y el RUT si falta</>}
          {resumen.repetidos > 0 && <><br />↩︎ {resumen.repetidos.toLocaleString('es-CL')} ya estaban en su caso y se saltaron</>}
        </div>
        {noImportados.length > 0 && (
          <div style={{ marginTop: 14, background: 'rgba(229,57,53,0.08)', border: '1px solid rgba(229,57,53,0.3)', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ color: '#EF9A9A', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              {noImportados.length.toLocaleString('es-CL')} archivo{noImportados.length !== 1 ? 's' : ''} NO se importaron. Vuelve a arrastrar lo mismo para reintentarlos: lo ya subido se salta.
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: 12, color: '#EF9A9A', lineHeight: 1.6 }}>
              {noImportados.slice(0, 200).map((f, i) => <div key={i}>{[f.carpeta, f.codigo, f.archivo].filter(Boolean).join(' / ')}: {f.resultado.replace('NO IMPORTADO: ', '')}</div>)}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
          <button className="p-btn-primary" onClick={descargarInforme}>⬇ Descargar informe ({informe.length.toLocaleString('es-CL')} archivos)</button>
          <button className="p-btn-secondary" onClick={reiniciar}>Importar otra carpeta</button>
        </div>
        <div style={{ fontSize: 12, color: '#9BB0CC', marginTop: 8 }}>
          El informe (se abre en Excel) dice qué pasó con cada archivo: a qué caso fue, o por qué no se importó.
        </div>
      </div>
    );
  }

  /* ── Revisar el cruce ───────────────────────────────────────────────── */
  const visibles = grupos.filter(g =>
    filtro === 'todas' ? true
      : filtro === 'revisar' ? !destinoFinal(g)
        : !manual.has(g.clave) && g.certeza === filtro);
  const totalArchivos = lista.length + descartados.basura.length + descartados.deGoogle.length;
  const chip = (f: Filtro, texto: string, n: number) => (
    <button key={f} onClick={() => setFiltro(f)} className="p-btn-secondary"
      style={{ fontSize: 12, padding: '4px 10px', opacity: filtro === f ? 1 : 0.6, borderColor: filtro === f ? 'var(--gold)' : undefined }}>
      {texto} ({n})
    </button>
  );
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div className="p-section-title" style={{ marginBottom: 0 }}>Revisa a qué caso va cada carpeta</div>
        <button className="p-btn-secondary" onClick={reiniciar}>Elegir otra carpeta</button>
      </div>

      <div style={{ fontSize: 13, color: '#9BB0CC', lineHeight: 1.7, marginBottom: 12 }}>
        {totalArchivos.toLocaleString('es-CL')} archivos encontrados · {lista.length.toLocaleString('es-CL')} para importar ({fmtBytes(lista.reduce((n, x) => n + x.archivo.size, 0))}) · {grupos.length} casos
        {descartados.basura.length > 0 && <> · {descartados.basura.length} del sistema o vacíos, ignorados</>}
        {descartados.deGoogle.length > 0 && (
          <div style={{ color: '#FFB74D' }}>
            ⚠️ {descartados.deGoogle.length} Google Docs/Sheets son accesos directos y no se pueden importar así: descarga la carpeta
            desde drive.google.com y arrastra el .zip, que los trae convertidos.
          </div>
        )}
        {sueltos.length > 0 && (
          <div style={{ color: '#FFB74D' }}>⚠️ {sueltos.length} archivo{sueltos.length !== 1 ? 's' : ''} fuera de una carpeta de cliente no se importa{sueltos.length !== 1 ? 'n' : ''} (quedan en el informe).</div>
        )}
      </div>

      {niveles.length > 1 && (
        <div className="p-field" style={{ maxWidth: 760, marginBottom: 14 }}>
          <div className="p-field-label">¿Dónde están las carpetas de los clientes?</div>
          <select className="p-select" value={nivel} onChange={e => setNivel(Number(e.target.value))}>
            {niveles.map(d => <option key={d} value={d}>{ejemplo(d)}</option>)}
          </select>
        </div>
      )}

      {nuevos.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14, padding: '10px 14px', background: 'rgba(100,181,246,0.06)', border: '1px solid rgba(100,181,246,0.3)', borderRadius: 8 }}>
          <div style={{ fontSize: 12.5, color: '#D6E4F4', flexBasis: '100%' }}>Los {nuevos.length} casos nuevos se crean con el nombre de la carpeta como cliente (y su RUT, si viene en el nombre), y con:</div>
          <div className="p-field" style={{ marginBottom: 0, minWidth: 220 }}>
            <div className="p-field-label">Abogado asignado</div>
            <select className="p-select" value={nuevoAbogado} onChange={e => setNuevoAbogado(e.target.value)}>
              <option value="">Sin asignar</option>
              {abogadas.map(a => <option key={a.id} value={a.id}>{a.nombre || a.email}</option>)}
            </select>
          </div>
          <div className="p-field" style={{ marginBottom: 0, minWidth: 220 }}>
            <div className="p-field-label">Captador{captadorObligatorio ? ' (obligatorio en tu estudio)' : ''}</div>
            <select className="p-select" value={nuevoCaptador} onChange={e => setNuevoCaptador(e.target.value)}>
              <option value="">{captadorObligatorio ? '— Seleccionar —' : 'Sin captador'}</option>
              {captadoras.map(c => <option key={c.id} value={c.id}>{c.nombre || c.email}</option>)}
            </select>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        {chip('todas', 'Todas', grupos.length)}
        {chip('revisar', '⚠ Sin caso elegido', porRevisar.length)}
        {chip('nombre', '≈ Por nombre', cuenta('nombre'))}
        {chip('nuevo', '＋ Nuevos', cuenta('nuevo'))}
        <span style={{ fontSize: 12, color: '#9BB0CC' }}>
          {cuenta('codigo') + cuenta('carpeta')} por código o ya importadas: seguras.
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="p-table" style={{ minWidth: 720 }}>
          <thead>
            <tr><th>Carpeta</th><th style={{ textAlign: 'right' }}>Archivos</th><th>Estado</th><th>Caso</th></tr>
          </thead>
          <tbody>
            {visibles.map(g => {
              const v = destino[g.clave] || '';
              const est = ESTADO[estadoDe(g)];
              const candidatos = g.candidatos.map(c => casoPorId.get(c.casoId)).filter(Boolean);
              const hermanos = grupos.filter(o => o.carpeta === g.carpeta && o.codigo && o.clave !== g.clave);
              return (
                <tr key={g.clave} style={{ opacity: v === OMITIR ? 0.45 : 1 }}>
                  <td style={{ fontSize: 13, color: '#D6E4F4' }}>
                    📁 {g.carpeta}
                    {g.codigo && <div style={{ fontSize: 11.5, color: '#9BB0CC', paddingLeft: 18 }}>└ {g.codigo}</div>}
                    {!g.codigo && hermanos.length > 0 && <div style={{ fontSize: 11.5, color: '#9BB0CC', paddingLeft: 18 }}>└ archivos sueltos del cliente</div>}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: 12, color: '#9BB0CC' }}
                    title={g.archivos.slice(0, 30).map(a => a.nombre).join('\n') + (g.archivos.length > 30 ? `\n… y ${g.archivos.length - 30} más` : '')}>
                    {g.archivos.length} · {fmtBytes(g.bytes)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: est.color }} title={est.ayuda}>{est.texto}</td>
                  <td style={{ minWidth: 300 }}>
                    <select className="p-select" value={v} onChange={e => elegir(g.clave, e.target.value)}
                      style={{ fontSize: 12.5, borderColor: destinoFinal(g) ? undefined : 'rgba(255,183,77,0.6)' }}>
                      <option value="">— Elegir caso —</option>
                      {candidatos.length > 0 && (
                        <optgroup label="Parecidos">
                          {candidatos.map((c: any) => <option key={c.id} value={c.id}>{etiquetaCaso(c)}</option>)}
                        </optgroup>
                      )}
                      {hermanos.length > 0 && (
                        <optgroup label="Con una causa de esta misma carpeta">
                          {hermanos.map(h => <option key={h.clave} value={JUNTO + h.clave}>Junto con {h.codigo}</option>)}
                        </optgroup>
                      )}
                      <option value={NUEVO}>➕ Crear caso nuevo: {nombreDeCliente(g.carpeta).nombre}{g.codigo ? ` (${g.codigo})` : ''}</option>
                      <option value={OMITIR}>No importar esta carpeta</option>
                      <optgroup label="Todos los casos">
                        {casosOrdenados.map(c => <option key={c.id} value={c.id}>{etiquetaCaso(c)}</option>)}
                      </optgroup>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 }}>
        <button className="p-btn-primary" disabled={!conDestino.length || faltaCaptador} onClick={importar}
          style={{ opacity: !conDestino.length || faltaCaptador ? 0.5 : 1 }}>
          Importar {archivosAImportar.toLocaleString('es-CL')} archivo{archivosAImportar !== 1 ? 's' : ''}
        </button>
        <span style={{ fontSize: 12, color: '#9BB0CC' }}>
          {fmtBytes(bytesAImportar)} · {aExistentes} caso{aExistentes !== 1 ? 's' : ''} existente{aExistentes !== 1 ? 's' : ''}
          {nuevos.length > 0 && ` · ${nuevos.length} nuevo${nuevos.length !== 1 ? 's' : ''}`}
          {porRevisar.length > 0 && ` · ${porRevisar.length} sin caso elegido no se importa${porRevisar.length !== 1 ? 'n' : ''}`}
          {faltaCaptador && ' · elige el captador de los casos nuevos'}
        </span>
      </div>
    </div>
  );
};

export default ImportarDocumentos;
